import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Target } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TaskGrid } from '@/components/tasks/TaskGrid';
import { goalService } from '@/lib/api/goal.service';
import { blockerService } from '@/lib/api/blocker.service';
import { eventService } from '@/lib/api/event.service';
import { taskService } from '@/lib/api/task.service';
import { userService } from '@/lib/api/user.service';
import { handleApiError } from '@/lib/api/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { GoalInstanceWithDetails } from '@/types/goal.types';
import type { GoalTemplate } from '@/types/goal.types';
import type { Blocker, BlockerMode } from '@/types/blocker.types';
import type { Event } from '@/types/event.types';
import type { Task } from '@/types/task.types';

const getGoalStatusBadge = (status: string, totalTasks = 0) => {
  if (status === 'new') {
    return <Badge variant="outline">Awaiting Initialization</Badge>;
  }
  if (status === 'not_started') {
    return totalTasks > 0 ? <Badge variant="secondary">Initialized</Badge> : <Badge variant="outline">Awaiting Initialization</Badge>;
  }
  if (status === 'initialized') {
    return <Badge variant="secondary">Initialized</Badge>;
  }
  if (status === 'completed') {
    return <Badge variant="active">Completed</Badge>;
  }
  if (status === 'blocked') {
    return <Badge variant="destructive">Blocked</Badge>;
  }
  if (status === 'cancelled') {
    return <Badge variant="outline">Cancelled</Badge>;
  }
  return <Badge variant="secondary">{status.replace('_', ' ')}</Badge>;
};

type GoalBlockedByEntityType = 'task' | 'goal' | 'event';
type GoalBlockingEntityType = 'task' | 'goal' | 'event';
type BlockerEditorMode = 'blockedBy' | 'blocking';

interface BlockedByInlineFormState {
  entityType: GoalBlockedByEntityType;
  entityId: string;
  eventStatus: string;
  mode: BlockerMode;
}

interface BlockingInlineFormState {
  entityType: GoalBlockingEntityType;
  entityId: string;
  eventStatus: string;
  mode: BlockerMode;
}

interface EntityOption {
  id: number;
  label: string;
}

const EVENT_STATUS_OPTIONS = ['draft', 'planning', 'planned', 'active', 'completed', 'cancelled'] as const;

export const GoalDetailPage: React.FC = () => {
  const { t } = useTranslation(['goals', 'common']);
  const { goalId, celebrationId, eventId } = useParams<{ goalId: string; celebrationId?: string; eventId?: string }>();
  const navigate = useNavigate();

  const goalIdNum = useMemo(() => Number.parseInt(goalId || '', 10), [goalId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(false);
  const [showInitializeDialog, setShowInitializeDialog] = useState(false);
  const [initializeTaskCount, setInitializeTaskCount] = useState(0);
  const [goal, setGoal] = useState<GoalInstanceWithDetails | null>(null);
  const [goalTemplate, setGoalTemplate] = useState<GoalTemplate | null>(null);
  const [blockedByBlockers, setBlockedByBlockers] = useState<Blocker[]>([]);
  const [blockingBlockers, setBlockingBlockers] = useState<Blocker[]>([]);
  const [eventRef, setEventRef] = useState<Event | null>(null);
  const [celebrationTasksForBlockers, setCelebrationTasksForBlockers] = useState<Task[]>([]);
  const [celebrationGoalsForBlockers, setCelebrationGoalsForBlockers] = useState<GoalInstanceWithDetails[]>([]);
  const [celebrationEventsForBlockers, setCelebrationEventsForBlockers] = useState<Event[]>([]);
  const [activeBlockerEditor, setActiveBlockerEditor] = useState<BlockerEditorMode | null>(null);
  const [blockedByForm, setBlockedByForm] = useState<BlockedByInlineFormState>({
    entityType: 'task',
    entityId: '',
    eventStatus: 'active',
    mode: 'hard',
  });
  const [blockingForm, setBlockingForm] = useState<BlockingInlineFormState>({
    entityType: 'task',
    entityId: '',
    eventStatus: 'planned',
    mode: 'hard',
  });
  const [savingBlocker, setSavingBlocker] = useState(false);
  const [deletingBlockerId, setDeletingBlockerId] = useState<number | null>(null);
  const [blockersLoading, setBlockersLoading] = useState(false);
  const [blockersError, setBlockersError] = useState<string | null>(null);
  const [editingDueDate, setEditingDueDate] = useState<number | null>(null);
  const [usernames, setUsernames] = useState<Record<number, string>>({});
  const { toast } = useToast();

  const loadGoal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [goalData, blockerData] = await Promise.all([
        goalService.getInstanceById(goalIdNum),
        blockerService.listByBlockedEntity('goal', goalIdNum),
      ]);

      setGoal(goalData);
      setBlockedByBlockers(blockerData);

      if ((goalData.status === 'new' || goalData.status === 'not_started') && (goalData.tasks?.length || 0) === 0) {
        try {
          setGoalTemplate(await goalService.getDefinitionById(goalData.goal_template_id));
        } catch (templateError) {
          void templateError;
          setGoalTemplate(null);
        }
      } else {
        setGoalTemplate(null);
      }

      if (goalData.entity_type === 'event') {
        const fetchedEvent = await eventService.getEvent(goalData.entity_id);
        setEventRef(fetchedEvent);
      } else {
        setEventRef(null);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [goalIdNum]);

  useEffect(() => {
    if (!Number.isFinite(goalIdNum) || goalIdNum <= 0) {
      setError('Invalid goal ID.');
      setLoading(false);
      return;
    }

    void loadGoal();
  }, [goalIdNum, loadGoal]);

  useEffect(() => {
    void userService.getAllUsers()
      .then((users) => {
        setUsernames(Object.fromEntries(users.map((user) => [user.id, user.full_name])));
      })
      .catch(() => undefined);
  }, []);

  const completedTasks = goal?.tasks?.filter((taskRef) => taskRef.task?.status === 'completed').length || 0;
  const templateTasks = goalTemplate?.task_templates ?? [];
  const showTemplateTasks = !goal?.tasks || goal.tasks.length === 0;
  const totalTasks = goal?.tasks?.length || 0;
  const hardBlockers = blockedByBlockers.filter((b) => b.mode === 'hard').length;
  const softBlockers = blockedByBlockers.filter((b) => b.mode === 'soft').length;

  const celebrationContextId = useMemo(() => {
    if (goal?.entity_type === 'celebration') {
      return goal.entity_id;
    }
    if (goal?.entity_type === 'event') {
      return eventRef?.celebration_id;
    }
    const fromRoute = Number.parseInt(celebrationId || '', 10);
    if (Number.isFinite(fromRoute) && fromRoute > 0) {
      return fromRoute;
    }
    return undefined;
  }, [goal, eventRef, celebrationId]);

  const tasksRoute = useMemo(() => {
    if (!goal) {
      return undefined;
    }
    if (goal.entity_type === 'celebration') {
      return `/celebrations/${goal.entity_id}/tasks`;
    }
    if (goal.entity_type === 'event') {
      return `/events/${goal.entity_id}/tasks`;
    }
    return undefined;
  }, [goal]);

  const entityOptionsByType = useMemo<Record<'task' | 'goal' | 'event', EntityOption[]>>(
    () => ({
      task: celebrationTasksForBlockers.map((task) => ({ id: task.id, label: task.title })),
      goal: celebrationGoalsForBlockers.map((instance) => ({
        id: instance.id,
        label: instance.template?.name || `Goal ${instance.id}`,
      })),
      event: celebrationEventsForBlockers.map((event) => ({ id: event.id, label: event.title })),
    }),
    [celebrationTasksForBlockers, celebrationGoalsForBlockers, celebrationEventsForBlockers]
  );

  const getEntityOptionsForEditor = (formState: { entityType: 'task' | 'goal' | 'event'; entityId: string }): EntityOption[] => {
    const options = entityOptionsByType[formState.entityType] || [];
    if (formState.entityType !== 'goal' || !goal) {
      return options;
    }

    return options.filter((option) => option.id !== goal.id);
  };

  const getEntityLabel = (entityType: string, entityId: number | null): string => {
    if (entityId === null || entityId === undefined) {
      return 'n/a';
    }

    if (entityType === 'task') {
      const taskOption = entityOptionsByType.task.find((option) => option.id === entityId);
      return taskOption ? taskOption.label : `Task #${entityId}`;
    }

    if (entityType === 'goal') {
      const goalOption = entityOptionsByType.goal.find((option) => option.id === entityId);
      return goalOption ? goalOption.label : `Goal #${entityId}`;
    }

    if (entityType === 'event') {
      const eventOption = entityOptionsByType.event.find((option) => option.id === entityId);
      return eventOption ? eventOption.label : `Event #${entityId}`;
    }

    return `#${entityId}`;
  };

  const fetchBlockingBlockers = async (currentGoalId: number, scopedCelebrationId: number): Promise<Blocker[]> => {
    const celebrationBlockers = await blockerService.listByCelebration(scopedCelebrationId);
    return celebrationBlockers.filter((blocker) => blocker.blocker_type === 'goal' && blocker.blocker_id === currentGoalId);
  };

  const loadGoalBlockerContext = async () => {
    if (!goal || !celebrationContextId) {
      return;
    }

    try {
      setBlockersLoading(true);
      setBlockersError(null);

      const [tasksForScope, goalsForScope, allEvents] = await Promise.all([
        taskService.getCelebrationTasks(celebrationContextId),
        goalService.getInstancesByEntity('celebration', celebrationContextId),
        eventService.getEvents(),
      ]);

      const eventsForScope = allEvents.filter((event) => event.celebration_id === celebrationContextId);
      const blockedBy = await blockerService.listByBlockedEntity('goal', goal.id);
      const blocking = await fetchBlockingBlockers(goal.id, celebrationContextId);

      setCelebrationTasksForBlockers(tasksForScope);
      setCelebrationGoalsForBlockers(goalsForScope);
      setCelebrationEventsForBlockers(eventsForScope);
      setBlockedByBlockers(blockedBy);
      setBlockingBlockers(blocking);
    } catch (err) {
      setBlockersError(handleApiError(err));
      setBlockingBlockers([]);
    } finally {
      setBlockersLoading(false);
    }
  };

  useEffect(() => {
    if (!goal || !celebrationContextId) {
      return;
    }

    void loadGoalBlockerContext();
  }, [goal?.id, celebrationContextId]);

  const createGoalBlocker = async (editorMode: BlockerEditorMode) => {
    if (!goal) {
      return;
    }

    const selectedEntityId = Number.parseInt((editorMode === 'blockedBy' ? blockedByForm.entityId : blockingForm.entityId), 10);
    if (!Number.isFinite(selectedEntityId) || selectedEntityId <= 0) {
      setBlockersError('Please select a valid entity before saving blocker.');
      return;
    }

    try {
      setSavingBlocker(true);
      setBlockersError(null);

      if (editorMode === 'blockedBy') {
        const formState = blockedByForm;
        await blockerService.createBlocker({
          blocked_entity: 'goal',
          blocked_id: goal.id,
          blocker_type: formState.entityType,
          blocker_id: selectedEntityId,
          blocker_event_status: formState.entityType === 'event' ? formState.eventStatus : undefined,
          mode: formState.mode,
        });
      } else {
        const formState = blockingForm;
        await blockerService.createBlocker({
          blocked_entity: formState.entityType,
          blocked_id: selectedEntityId,
          blocked_event_status: formState.entityType === 'event' ? formState.eventStatus : undefined,
          blocker_type: 'goal',
          blocker_id: goal.id,
          mode: formState.mode,
        });
      }

      setActiveBlockerEditor(null);
      setBlockedByForm({ entityType: 'task', entityId: '', eventStatus: 'active', mode: 'hard' });
      setBlockingForm({ entityType: 'task', entityId: '', eventStatus: 'planned', mode: 'hard' });
      await loadGoalBlockerContext();
    } catch (err) {
      setBlockersError(handleApiError(err));
    } finally {
      setSavingBlocker(false);
    }
  };

  const deleteGoalBlocker = async (blockerId: number) => {
    try {
      setDeletingBlockerId(blockerId);
      setBlockersError(null);
      await blockerService.deleteBlocker(blockerId);
      await loadGoalBlockerContext();
    } catch (err) {
      setBlockersError(handleApiError(err));
    } finally {
      setDeletingBlockerId(null);
    }
  };

  const goalTaskCount = goal?.progress?.total_tasks ?? goal?.total_tasks ?? goal?.tasks?.length ?? 0;

  const openInitializeDialog = async () => {
    if (!goal) {
      return;
    }

    try {
      const definition = await goalService.getDefinitionById(goal.goal_template_id);
      setInitializeTaskCount(definition.task_templates?.length || 0);
      setShowInitializeDialog(true);
    } catch (err) {
      const message = handleApiError(err);
      toast({ title: 'Unable to prepare initialization', description: message, variant: 'destructive' });
    }
  };

  const confirmInitialize = async () => {
    if (!goal) {
      return;
    }

    try {
      setInitializing(true);
      await goalService.initializeInstance(goal.id);
      setShowInitializeDialog(false);
      await loadGoal();
      toast({
        title: 'Goal initialized',
        description: `${initializeTaskCount} task${initializeTaskCount === 1 ? '' : 's'} created and linked to this goal.`,
      });
    } catch (err) {
      const message = handleApiError(err);
      toast({ title: 'Failed to initialize goal', description: message, variant: 'destructive' });
    } finally {
      setInitializing(false);
    }
  };

  const updateGoalTask = async (taskId: number, payload: Record<string, unknown>) => {
    if (!goal) {
      return;
    }

    try {
      if (goal.entity_type === 'event') {
        await taskService.updateTask(goal.entity_id, taskId, payload);
      } else {
        await taskService.updateCelebrationTask(goal.entity_id, taskId, payload);
      }
      await loadGoal();
    } catch (err) {
      toast({ title: 'Failed to update task', description: handleApiError(err), variant: 'destructive' });
    }
  };

  const handleTaskStatusChange = (taskId: number, status: string) => {
    void updateGoalTask(taskId, { status });
  };

  const handleTaskDueDateChange = (task: Task, newDate: string) => {
    setEditingDueDate(null);
    void updateGoalTask(task.id, { due_date: newDate });
  };

  const handleDeleteGoalTask = async (taskId: number) => {
    if (!goal || !window.confirm('Delete this task?')) {
      return;
    }

    try {
      if (goal.entity_type === 'event') {
        await taskService.deleteTask(goal.entity_id, taskId);
      } else {
        await taskService.deleteCelebrationTask(goal.entity_id, taskId);
      }
      await loadGoal();
    } catch (err) {
      toast({ title: 'Failed to delete task', description: handleApiError(err), variant: 'destructive' });
    }
  };

  const handleEditGoalTask = (task: Task) => {
    if (tasksRoute) {
      navigate(`${tasksRoute}?taskId=${task.id}`);
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="default"
        title="Loading goal details"
        description="Please wait while we load goal, tasks, and blockers."
      />
    );
  }

  if (error || !goal) {
    return (
      <PageState
        kind="error"
        width="default"
        title="Goal details unavailable"
        description={error || 'Goal not found.'}
        action={
          <Button size="sm" variant="outline" onClick={() => navigate('/celebrations')}>
            Back
          </Button>
        }
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={[
          { label: t('common:nav.home'), to: '/home' },
          { label: t('common:nav.celebrations'), to: '/celebrations' },
          ...(celebrationContextId ? [{ label: 'Celebration', to: `/celebrations/${celebrationContextId}` }] : []),
          ...(goal.entity_type === 'event' && eventId ? [{ label: 'Event', to: `/events/${eventId}` }] : []),
          { label: 'Goal Detail' },
        ]}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">{goal.template?.name || `Goal #${goal.id}`}</CardTitle>
                {goal.template?.description && (
                  <p className="text-sm text-muted-foreground mt-1">{goal.template.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
                {getGoalStatusBadge(goal.status, goalTaskCount)}
              {(goal.status === 'new' || (goal.status === 'not_started' && goalTaskCount === 0)) && (
                <Button size="sm" onClick={() => void openInitializeDialog()} disabled={initializing}>
                  Initialize Goal
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Tasks</p>
              <p className="text-lg font-semibold">{totalTasks}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-lg font-semibold">{completedTasks}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Hard Blockers</p>
              <p className="text-lg font-semibold">{hardBlockers}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Soft Blockers</p>
              <p className="text-lg font-semibold">{softBlockers}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">Tasks</CardTitle>
            {tasksRoute && goal.status !== 'new' && (
              <Button size="sm" variant="outline" onClick={() => navigate(tasksRoute)}>
                Manage Tasks
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showTemplateTasks && templateTasks.length > 0 ? (
            <div className="space-y-3">
              <div className="space-y-2">
                {templateTasks.map((templateTask) => (
                  <div key={templateTask.id} className="rounded-md border border-dashed bg-muted/20 px-3 py-2">
                    <p className="text-sm italic text-muted-foreground">
                      {templateTask.task_title || `Task #${templateTask.id}`}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs font-medium italic text-primary">
                * Tasks will be added when the goal is initialized.
              </p>
            </div>
          ) : !goal.tasks || goal.tasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {goal.status === 'new'
                ? 'No tasks linked yet. Initialize this goal to create and associate tasks.'
                : 'No tasks linked to this goal.'}
            </p>
          ) : (
            <TaskGrid
              tasks={goal.tasks.map(taskRef => taskRef.task).filter((task): task is NonNullable<typeof task> => task !== null && task !== undefined) as any}
              showGoalColumn={true}
              showBlockers={true}
              editingDueDate={editingDueDate}
              onEditingDueDateChange={setEditingDueDate}
              usernames={usernames}
              onStatusChange={handleTaskStatusChange}
              onDueDateChange={handleTaskDueDateChange}
              onEditTask={handleEditGoalTask}
              onDeleteTask={handleDeleteGoalTask}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Blockers</CardTitle>
        </CardHeader>
        <CardContent>
          {blockersError && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {blockersError}
            </div>
          )}

          {!celebrationContextId ? (
            <p className="text-sm text-muted-foreground">Blocker editing is unavailable without celebration context.</p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border bg-white p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Blocked by</p>
                  <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(activeBlockerEditor === 'blockedBy' ? null : 'blockedBy')}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>

                {blockersLoading ? (
                  <p className="text-xs text-muted-foreground">Loading blockers...</p>
                ) : blockedByBlockers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No blockers are currently blocking this goal.</p>
                ) : (
                  <div className="space-y-1.5">
                    {blockedByBlockers.map((blocker) => (
                      <div key={blocker.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate">{getEntityLabel(blocker.blocker_type, blocker.blocker_id)}</p>
                          <p className="text-muted-foreground">
                            mode: {blocker.mode}
                            {blocker.blocker_type === 'event' && blocker.blocker_event_status
                              ? ` · requires event >= ${blocker.blocker_event_status}`
                              : ''}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className="text-destructive"
                          disabled={deletingBlockerId === blocker.id}
                          onClick={() => void deleteGoalBlocker(blocker.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {activeBlockerEditor === 'blockedBy' && (
                  <div className="rounded-md border border-dashed p-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px]">Entity</label>
                        <select
                          value={blockedByForm.entityType}
                          onChange={(e) => setBlockedByForm((prev) => ({
                            ...prev,
                            entityType: e.target.value as GoalBlockedByEntityType,
                            entityId: '',
                            eventStatus: 'active',
                          }))}
                          title="Blocked by entity"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          <option value="task">Task</option>
                          <option value="goal">Goal</option>
                          <option value="event">Event</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px]">Mode</label>
                        <select
                          value={blockedByForm.mode}
                          onChange={(e) => setBlockedByForm((prev) => ({ ...prev, mode: e.target.value as BlockerMode }))}
                          title="Blocker mode"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          <option value="hard">Hard</option>
                          <option value="soft">Soft</option>
                        </select>
                      </div>
                    </div>

                    {blockedByForm.entityType === 'event' && (
                      <div>
                        <label className="text-[11px]">Required Event Status</label>
                        <select
                          value={blockedByForm.eventStatus}
                          onChange={(e) => setBlockedByForm((prev) => ({ ...prev, eventStatus: e.target.value }))}
                          title="Required blocker event status"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          {EVENT_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-[11px]">Name / Title</label>
                      <select
                        value={blockedByForm.entityId}
                        onChange={(e) => setBlockedByForm((prev) => ({ ...prev, entityId: e.target.value }))}
                        title="Blocked by target"
                        className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                      >
                        <option value="">Select {blockedByForm.entityType}</option>
                        {getEntityOptionsForEditor(blockedByForm).map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(null)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        disabled={savingBlocker || !blockedByForm.entityId}
                        onClick={() => void createGoalBlocker('blockedBy')}
                      >
                        {savingBlocker ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-md border bg-white p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Blocking</p>
                  <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(activeBlockerEditor === 'blocking' ? null : 'blocking')}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                </div>

                {blockersLoading ? (
                  <p className="text-xs text-muted-foreground">Loading blockers...</p>
                ) : blockingBlockers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">This goal is not currently blocking other entities.</p>
                ) : (
                  <div className="space-y-1.5">
                    {blockingBlockers.map((blocker) => (
                      <div key={blocker.id} className="rounded border px-2 py-1.5 text-xs flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate">{getEntityLabel(blocker.blocked_entity, blocker.blocked_id)}</p>
                          <p className="text-muted-foreground">
                            mode: {blocker.mode}
                            {blocker.blocked_entity === 'event' && blocker.blocked_event_status
                              ? ` · blocks event status >= ${blocker.blocked_event_status}`
                              : ''}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          className="text-destructive"
                          disabled={deletingBlockerId === blocker.id}
                          onClick={() => void deleteGoalBlocker(blocker.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {activeBlockerEditor === 'blocking' && (
                  <div className="rounded-md border border-dashed p-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[11px]">Entity</label>
                        <select
                          value={blockingForm.entityType}
                          onChange={(e) => setBlockingForm((prev) => ({
                            ...prev,
                            entityType: e.target.value as GoalBlockingEntityType,
                            entityId: '',
                            eventStatus: 'planned',
                          }))}
                          title="Blocking entity"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          <option value="task">Task</option>
                          <option value="goal">Goal</option>
                          <option value="event">Event</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px]">Mode</label>
                        <select
                          value={blockingForm.mode}
                          onChange={(e) => setBlockingForm((prev) => ({ ...prev, mode: e.target.value as BlockerMode }))}
                          title="Blocking mode"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          <option value="hard">Hard</option>
                          <option value="soft">Soft</option>
                        </select>
                      </div>
                    </div>

                    {blockingForm.entityType === 'event' && (
                      <div>
                        <label className="text-[11px]">Blocked Event Status</label>
                        <select
                          value={blockingForm.eventStatus}
                          onChange={(e) => setBlockingForm((prev) => ({ ...prev, eventStatus: e.target.value }))}
                          title="Blocked event target status"
                          className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                        >
                          {EVENT_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-[11px]">Name / Title</label>
                      <select
                        value={blockingForm.entityId}
                        onChange={(e) => setBlockingForm((prev) => ({ ...prev, entityId: e.target.value }))}
                        title="Blocking target"
                        className="w-full h-7 px-2 text-xs rounded-md border border-input bg-background"
                      >
                        <option value="">Select {blockingForm.entityType}</option>
                        {getEntityOptionsForEditor(blockingForm).map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" size="xs" onClick={() => setActiveBlockerEditor(null)}>
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="xs"
                        disabled={savingBlocker || !blockingForm.entityId}
                        onClick={() => void createGoalBlocker('blocking')}
                      >
                        {savingBlocker ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showInitializeDialog} onOpenChange={setShowInitializeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Initialize Goal</AlertDialogTitle>
            <AlertDialogDescription>
              This will create {initializeTaskCount} task{initializeTaskCount === 1 ? '' : 's'} and associate them with this goal. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={initializing} onClick={() => setShowInitializeDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={initializing} onClick={() => void confirmInitialize()}>
              {initializing ? 'Initializing...' : 'Initialize'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default GoalDetailPage;
